import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import { formatInr } from "../utils/formatInr";
import BulkActionsBar, { bulkIcons } from "../components/BulkActionsBar";
import BulkValueModal from "../components/BulkValueModal";
import "./ItemMaster.css";

const API = "/inventory";

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

// ── 1. Procurement 6-Field Product Modal ─────────────────────────────────────
function ProductModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState(
    item
      ? {
          name: item.name || "",
          size: item.size || "",
          rate: item.rate ? String(item.rate) : "",
          quantity: item.quantity !== undefined ? String(item.quantity) : "",
          vendor: item.vendor || "",
          notes: item.notes || "",
          // Advanced fields
          category: item.category || "Material",
          unit: item.unit || "Nos",
          minQuantity: item.minQuantity ? String(item.minQuantity) : "",
          location: item.location || "",
          sku: item.sku || "",
        }
      : {
          name: "",
          size: "",
          rate: "",
          quantity: "",
          vendor: "",
          notes: "",
          category: "Material",
          unit: "Nos",
          minQuantity: "",
          location: "",
          sku: "",
        }
  );
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const h = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  // Live stock value preview: Rate * Quantity
  const calculatedStockValue = useMemo(() => {
    const r = toNumber(form.rate);
    const q = toNumber(form.quantity);
    return Math.max(0, Math.round(r * q * 100) / 100);
  }, [form.rate, form.quantity]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation per developer specification
    if (!form.name.trim()) {
      setError("Product Name is required.");
      return;
    }
    if (!form.size.trim()) {
      setError("Size is required (e.g., 8 × 4 ft, 18 mm, 12 mm, 0 Crank).");
      return;
    }
    const rateVal = toNumber(form.rate);
    if (rateVal <= 0) {
      setError("Rate per unit must be greater than zero.");
      return;
    }
    const qtyVal = toNumber(form.quantity);
    if (qtyVal < 0) {
      setError("Quantity must be zero or greater.");
      return;
    }
    if (!form.vendor.trim()) {
      setError("Vendor name is required.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        size: form.size.trim(),
        rate: rateVal,
        quantity: qtyVal,
        vendor: form.vendor.trim(),
        notes: form.notes.trim(),
        category: form.category.trim() || "Material",
        unit: form.unit.trim() || "Nos",
        minQuantity: toNumber(form.minQuantity),
        location: form.location.trim(),
        sku: form.sku.trim(),
      };

      if (item) {
        await api.put(`${API}/${item._id}`, payload);
      } else {
        await api.post(API, payload);
      }

      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save product.");
    } finally {
      setSaving(false);
    }
  };

  const modalContent = (
    <div className="im-overlay" onClick={onClose}>
      <div className="im-modal" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
        <div className="im-modal-header">
          <div>
            <h3>{item ? "Edit Inventory Product" : "New Procurement Product"}</h3>
            <p style={{ margin: "2px 0 0 0", fontSize: 12, color: "#6B7280" }}>
              Standard 6-field material record for stock tracking and customer allotment.
            </p>
          </div>
          <button className="im-icon-btn" onClick={onClose} type="button" aria-label="Close">
            ✕
          </button>
        </div>

        <div className="im-modal-body">
          {error && (
            <div
              style={{
                background: "#FEF2F2",
                border: "1px solid #FECACA",
                color: "#991B1B",
                padding: "10px 14px",
                borderRadius: 8,
                marginBottom: 14,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          <form id="product-form" onSubmit={submit}>
            {/* Field 1: Product Name */}
            <div className="im-field">
              <label>
                Product Name <span className="im-req">*</span>
              </label>
              <input
                name="name"
                value={form.name}
                onChange={h}
                required
                placeholder="e.g. 18 mm Plywood, 19 mm Blockboard, Soft Close Hinge, Fevicol, Laminate"
              />
            </div>

            {/* Field 2 & Field 3: Size & Rate */}
            <div className="im-row">
              <div className="im-field">
                <label>
                  Size / Specification <span className="im-req">*</span>
                </label>
                <input
                  name="size"
                  value={form.size}
                  onChange={h}
                  required
                  placeholder="e.g. 8 × 4 ft, 18 mm, 12 mm, 0 Crank"
                />
              </div>

              <div className="im-field">
                <label>
                  Rate (₹ per unit) <span className="im-req">*</span>
                </label>
                <input
                  name="rate"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.rate}
                  onChange={h}
                  required
                  placeholder="e.g. 1200"
                />
              </div>
            </div>

            {/* Field 4 & Field 5: Quantity & Vendor */}
            <div className="im-row">
              <div className="im-field">
                <label>
                  Available Quantity in Stock <span className="im-req">*</span>
                </label>
                <input
                  name="quantity"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.quantity}
                  onChange={h}
                  required
                  placeholder="e.g. 50"
                />
              </div>

              <div className="im-field">
                <label>
                  Vendor / Supplier <span className="im-req">*</span>
                </label>
                <input
                  name="vendor"
                  value={form.vendor}
                  onChange={h}
                  required
                  placeholder="e.g. Onyx VNS Solutions, Century, Greenlam"
                />
              </div>
            </div>

            {/* Live Stock Value Calculation Banner */}
            <div
              style={{
                background: "#F8F5F9",
                border: "1px solid #E8DFEB",
                borderRadius: 8,
                padding: "10px 14px",
                marginBottom: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontSize: 12,
              }}
            >
              <div>
                <span style={{ color: "#4B5563" }}>Total Calculated Stock Value:</span>
                <span style={{ color: "#9CA3AF", marginLeft: 6 }}>(Rate × Quantity)</span>
              </div>
              <strong style={{ fontSize: 15, color: "#976793" }}>
                ₹ {formatInr(calculatedStockValue)}
              </strong>
            </div>

            {/* Field 6: Note */}
            <div className="im-field">
              <label>Note (Optional)</label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={h}
                rows={2}
                placeholder="Optional extra details about product or purchase (color, grade, batch, etc.)"
              />
            </div>

            {/* Collapsible Advanced Details */}
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#976793",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                {showAdvanced ? "▲ Hide Advanced Options" : "▼ More Options (Unit, Category, Min Stock, Location)"}
              </button>

              {showAdvanced && (
                <div
                  style={{
                    background: "#FAFAFA",
                    border: "1px solid #EEEEEE",
                    borderRadius: 8,
                    padding: "12px 14px",
                    marginTop: 10,
                  }}
                >
                  <div className="im-row">
                    <div className="im-field">
                      <label>Unit of Measure</label>
                      <input name="unit" value={form.unit} onChange={h} placeholder="e.g. Sheets, Sets, Pcs, Packets" />
                    </div>
                    <div className="im-field">
                      <label>Category</label>
                      <input name="category" value={form.category} onChange={h} placeholder="e.g. Hardware, Plywood, Laminate" />
                    </div>
                  </div>

                  <div className="im-row">
                    <div className="im-field">
                      <label>Low Stock Alert Level</label>
                      <input
                        name="minQuantity"
                        type="number"
                        step="0.01"
                        value={form.minQuantity}
                        onChange={h}
                        placeholder="Alert when stock falls below"
                      />
                    </div>
                    <div className="im-field">
                      <label>Warehouse Storage Location</label>
                      <input name="location" value={form.location} onChange={h} placeholder="e.g. Rack A-2, Timber Shed" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>

        <div className="im-modal-footer">
          <button type="button" className="im-btn-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form="product-form" className="im-btn-submit" disabled={saving}>
            {saving ? "Saving Product..." : item ? "Update Product" : "Add to Inventory"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

// ── 2. Allot Material / Hardware Modal (Multi-Item Allotment & Further Steps) ─
function AllotModal({ item, initialItems = [], allItems = [], onClose, onAllotted, onOpenHistory }) {
  const navigate = useNavigate();
  const [step, setStep] = useState("form"); // "form" | "further-steps"
  const [successData, setSuccessData] = useState(null);

  // Client Selection
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedClientId, setSelectedClientId] = useState("");
  const [clientOrders, setClientOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState("new");
  const [notes, setNotes] = useState("");

  // Allotment Items List (Multi-item list)
  const [itemsList, setItemsList] = useState(() => {
    const seed = Array.isArray(initialItems) && initialItems.length > 0
      ? initialItems
      : item
      ? [item]
      : [];
    return seed.map((it) => ({
      itemId: it._id,
      name: it.name || "",
      size: it.size || "",
      unit: it.unit || "Nos",
      vendor: it.vendor || "",
      maxStock: toNumber(it.quantity, 0),
      quantity: 1,
      rate: toNumber(it.rate, 0),
      notes: "",
    }));
  });

  // Item Picker State (Choose item -> add to list)
  const [pickerItemId, setPickerItemId] = useState("");
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerQty, setPickerQty] = useState("1");
  const [pickerRate, setPickerRate] = useState("");
  const [pickerNotes, setPickerNotes] = useState("");
  const [pickerError, setPickerError] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Selected picker item lookup
  const pickerItem = useMemo(() => {
    return allItems.find((i) => i._id === pickerItemId) || null;
  }, [allItems, pickerItemId]);

  // Keep picker rate in sync when picker item changes
  useEffect(() => {
    if (pickerItem) {
      setPickerRate(pickerItem.rate != null ? String(pickerItem.rate) : "");
      if (!pickerQty || toNumber(pickerQty, 0) <= 0) {
        setPickerQty("1");
      }
    } else {
      setPickerRate("");
    }
  }, [pickerItem]);

  // Load clients
  useEffect(() => {
    setLoadingClients(true);
    api
      .get("/clients", { params: { limit: 500 } })
      .then((res) => setClients(res.data?.data?.clients || []))
      .catch((err) => console.error(err))
      .finally(() => setLoadingClients(false));
  }, []);

  const filteredClients = useMemo(() => {
    if (!customerSearch.trim()) return clients;
    const q = customerSearch.toLowerCase().trim();
    return clients.filter((c) => {
      const name = (c.name || "").toLowerCase();
      const phone = (c.phone || "").toLowerCase();
      const email = (c.email || "").toLowerCase();
      const city = (c.city || "").toLowerCase();
      const company = (c.company || "").toLowerCase();
      return name.includes(q) || phone.includes(q) || email.includes(q) || city.includes(q) || company.includes(q);
    });
  }, [clients, customerSearch]);

  const selectedClient = useMemo(() => {
    return clients.find((c) => c._id === selectedClientId) || null;
  }, [clients, selectedClientId]);

  // Load client's procurement orders when client is selected
  useEffect(() => {
    if (!selectedClientId) {
      setClientOrders([]);
      setSelectedOrderId("new");
      return;
    }
    setLoadingOrders(true);
    api
      .get("/orders", { params: { client: selectedClientId, orderType: "Procurement", limit: 50 } })
      .then((res) => {
        const ords = res.data?.data?.orders || [];
        setClientOrders(ords);
      })
      .catch((err) => {
        console.error("Failed to load client orders", err);
        setClientOrders([]);
      })
      .finally(() => setLoadingOrders(false));
  }, [selectedClientId]);

  // Filter items in dropdown
  const filteredPickerItems = useMemo(() => {
    if (!pickerSearch.trim()) return allItems;
    const q = pickerSearch.toLowerCase().trim();
    return allItems.filter((i) => {
      const name = (i.name || "").toLowerCase();
      const size = (i.size || "").toLowerCase();
      const vendor = (i.vendor || "").toLowerCase();
      const sku = (i.sku || "").toLowerCase();
      return name.includes(q) || size.includes(q) || vendor.includes(q) || sku.includes(q);
    });
  }, [allItems, pickerSearch]);

  // Add Item from Picker into Allotment List
  const handleAddItemToList = (e) => {
    if (e) e.preventDefault();
    setPickerError("");

    if (!pickerItemId || !pickerItem) {
      setPickerError("Please choose a material / product from the dropdown.");
      return;
    }
    const q = toNumber(pickerQty, 0);
    if (q <= 0) {
      setPickerError("Quantity to allot must be greater than zero.");
      return;
    }
    if (q > pickerItem.quantity) {
      setPickerError(`Cannot allot ${q}. Only ${pickerItem.quantity} ${pickerItem.unit || "units"} available in store stock.`);
      return;
    }
    const r = toNumber(pickerRate, 0);
    if (r < 0) {
      setPickerError("Unit rate cannot be negative.");
      return;
    }

    const existingIdx = itemsList.findIndex((i) => i.itemId === pickerItem._id);
    if (existingIdx >= 0) {
      const curQty = toNumber(itemsList[existingIdx].quantity, 0);
      const combinedQty = curQty + q;
      if (combinedQty > pickerItem.quantity) {
        setPickerError(
          `This item is already in list with ${curQty} units. Adding ${q} more exceeds store stock (${pickerItem.quantity}).`
        );
        return;
      }
      setItemsList((prev) =>
        prev.map((it, idx) =>
          idx === existingIdx
            ? {
                ...it,
                quantity: combinedQty,
                rate: r > 0 ? r : it.rate,
                notes: pickerNotes.trim() ? (it.notes ? `${it.notes}; ${pickerNotes.trim()}` : pickerNotes.trim()) : it.notes,
              }
            : it
        )
      );
    } else {
      setItemsList((prev) => [
        ...prev,
        {
          itemId: pickerItem._id,
          name: pickerItem.name,
          size: pickerItem.size || "",
          unit: pickerItem.unit || "Nos",
          vendor: pickerItem.vendor || "",
          maxStock: toNumber(pickerItem.quantity, 0),
          quantity: q,
          rate: r,
          notes: pickerNotes.trim(),
        },
      ]);
    }

    // Reset picker inputs for next item
    setPickerItemId("");
    setPickerSearch("");
    setPickerQty("1");
    setPickerRate("");
    setPickerNotes("");
    setPickerError("");
  };

  const updateItemQty = (index, val) => {
    setItemsList((prev) =>
      prev.map((it, idx) => (idx === index ? { ...it, quantity: val } : it))
    );
  };

  const updateItemRate = (index, val) => {
    setItemsList((prev) =>
      prev.map((it, idx) => (idx === index ? { ...it, rate: val } : it))
    );
  };

  const updateItemNotes = (index, val) => {
    setItemsList((prev) =>
      prev.map((it, idx) => (idx === index ? { ...it, notes: val } : it))
    );
  };

  const removeItem = (index) => {
    setItemsList((prev) => prev.filter((_, idx) => idx !== index));
  };

  // Grand total and validation checks
  const { grandTotal, totalUnits, hasInvalidItems } = useMemo(() => {
    let total = 0;
    let units = 0;
    let invalid = false;

    itemsList.forEach((it) => {
      const q = toNumber(it.quantity, 0);
      const r = toNumber(it.rate, 0);
      if (q <= 0 || q > it.maxStock || r < 0) {
        invalid = true;
      }
      total += q * r;
      units += q;
    });

    return {
      grandTotal: Math.round(total * 100) / 100,
      totalUnits: Math.round(units * 100) / 100,
      hasInvalidItems: invalid,
    };
  }, [itemsList]);

  // Submit Allotment
  const submit = async (e) => {
    if (e) e.preventDefault();
    setError("");

    if (!selectedClientId) {
      setError("Please select a customer / client.");
      return;
    }
    if (itemsList.length === 0) {
      setError("Please add at least one item to the allotment list.");
      return;
    }
    if (hasInvalidItems) {
      setError("Please check item quantities and rates. Quantities must be > 0 and cannot exceed store stock.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        clientId: selectedClientId,
        clientName: selectedClient?.name || undefined,
        orderId: selectedOrderId,
        notes: notes.trim() || undefined,
        items: itemsList.map((it) => ({
          itemId: it.itemId,
          quantity: toNumber(it.quantity, 0),
          rate: toNumber(it.rate, 0),
          notes: it.notes ? it.notes.trim() : undefined,
        })),
      };

      const res = await api.post("/inventory/bulk-issue", payload);
      const data = res.data?.data || {};

      setSuccessData(data);
      setStep("further-steps");
      onAllotted();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to allot materials.");
    } finally {
      setSubmitting(false);
    }
  };

  const modalContent = (
    <div className="im-overlay" onClick={onClose}>
      <div
        className="im-modal"
        style={{ maxWidth: 740, width: "95vw", maxHeight: "90vh", display: "flex", flexDirection: "column" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="im-modal-header" style={{ padding: "16px 20px", borderBottom: "1px solid #E5E7EB" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, color: "#1F2937" }}>
              {step === "further-steps" ? "🎉 Material Allotment Confirmed!" : "Allot Material / Hardware to Customer"}
            </h3>
            <p style={{ margin: "3px 0 0 0", fontSize: 12, color: "#6B7280" }}>
              {step === "further-steps"
                ? "Items auto-deducted from store stock and logged into client purchase orders."
                : "Choose items to build an allotment list, auto-deduct stock, and generate purchase records."}
            </p>
          </div>
          <button className="im-icon-btn" onClick={onClose} type="button" aria-label="Close">
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <div className="im-modal-body" style={{ padding: "18px 20px", overflowY: "auto", flex: 1 }}>
          {error && (
            <div
              style={{
                background: "#FEF2F2",
                border: "1px solid #FECACA",
                color: "#991B1B",
                padding: "10px 14px",
                borderRadius: 8,
                marginBottom: 16,
                fontSize: 12,
              }}
            >
              {error}
            </div>
          )}

          {step === "form" ? (
            <div>
              {/* ── Customer / Client Selection ── */}
              <div
                style={{
                  background: "#F9FAFB",
                  border: "1px solid #E5E7EB",
                  borderRadius: 8,
                  padding: "12px 14px",
                  marginBottom: 16,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", margin: 0, textTransform: "uppercase" }}>
                    Select Customer / Client <span style={{ color: "#EF4444" }}>*</span>
                  </label>
                  {selectedClientId && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedClientId("");
                        setCustomerSearch("");
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#976793",
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      ✕ Change Customer
                    </button>
                  )}
                </div>

                <div style={{ position: "relative", marginBottom: 6 }}>
                  <span
                    style={{
                      position: "absolute",
                      left: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#9CA3AF",
                      fontSize: 12,
                      pointerEvents: "none",
                    }}
                  >
                    🔍
                  </span>
                  <input
                    type="text"
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    placeholder="Search customer by name, phone or city..."
                    style={{
                      width: "100%",
                      padding: "8px 30px 8px 30px",
                      fontSize: 12,
                      borderRadius: 6,
                      border: "1px solid #D1D5DB",
                      background: "#fff",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                  {customerSearch && (
                    <button
                      type="button"
                      onClick={() => setCustomerSearch("")}
                      style={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        color: "#9CA3AF",
                        cursor: "pointer",
                        fontSize: 12,
                        padding: "2px 4px",
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>

                {loadingClients ? (
                  <div style={{ fontSize: 12, color: "#6B7280", padding: "6px 0" }}>Loading customers list...</div>
                ) : (
                  <select
                    value={selectedClientId}
                    onChange={(e) => setSelectedClientId(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      borderRadius: 6,
                      border: "1px solid #D1D5DB",
                      fontSize: 13,
                      background: "#fff",
                    }}
                  >
                    <option value="">
                      {filteredClients.length === 0
                        ? "-- No customers match search --"
                        : `-- Choose Customer (${filteredClients.length} ${customerSearch ? "matches" : "available"}) --`}
                    </option>
                    {filteredClients.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.name} {c.phone ? `(${c.phone})` : ""} {c.city ? `• ${c.city}` : ""}
                      </option>
                    ))}
                  </select>
                )}

                {selectedClient && (
                  <div
                    style={{
                      marginTop: 8,
                      padding: "6px 10px",
                      background: "#F0FDF4",
                      border: "1px solid #BBF7D0",
                      borderRadius: 6,
                      fontSize: 11,
                      color: "#166534",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      <strong>✓ {selectedClient.name}</strong>
                      {selectedClient.phone && <span style={{ marginLeft: 6, color: "#4B5563" }}>📞 {selectedClient.phone}</span>}
                      {selectedClient.city && <span style={{ marginLeft: 6, color: "#6B7280" }}>📍 {selectedClient.city}</span>}
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#15803D", textTransform: "uppercase" }}>Selected</span>
                  </div>
                )}
              </div>

              {/* ── Section 1: Item Picker (Choose Item -> Add to List) ── */}
              <div
                style={{
                  background: "#FAF7FA",
                  border: "1px solid #EADBEC",
                  borderRadius: 8,
                  padding: "14px",
                  marginBottom: 18,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#2E1A46", textTransform: "uppercase" }}>
                    1. Choose Item to Add to Allotment
                  </div>
                  <span style={{ fontSize: 11, color: "#6B7280" }}>
                    Select an item, enter quantity & rate, then click "+ Add to List"
                  </span>
                </div>

                {pickerError && (
                  <div
                    style={{
                      background: "#FEF2F2",
                      border: "1px solid #FECACA",
                      color: "#991B1B",
                      padding: "6px 10px",
                      borderRadius: 6,
                      fontSize: 11,
                      marginBottom: 8,
                    }}
                  >
                    {pickerError}
                  </div>
                )}

                {/* Filter / Search Product */}
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <div style={{ flex: 1, position: "relative" }}>
                    <input
                      type="text"
                      value={pickerSearch}
                      onChange={(e) => setPickerSearch(e.target.value)}
                      placeholder="Filter items by name, size or vendor..."
                      style={{
                        width: "100%",
                        padding: "7px 10px",
                        fontSize: 12,
                        borderRadius: 6,
                        border: "1px solid #D1D5DB",
                        background: "#fff",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                </div>

                {/* Product Dropdown */}
                <select
                  value={pickerItemId}
                  onChange={(e) => setPickerItemId(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: 6,
                    border: "1px solid #D1D5DB",
                    fontSize: 13,
                    background: "#fff",
                    marginBottom: 10,
                  }}
                >
                  <option value="">-- Choose Product / Hardware ({filteredPickerItems.length} available) --</option>
                  {filteredPickerItems.map((i) => (
                    <option key={i._id} value={i._id} disabled={i.quantity <= 0}>
                      {i.name} {i.size ? `(${i.size})` : ""} — Stock: {i.quantity} {i.unit || "Nos"} (₹{formatInr(i.rate)}) {i.quantity <= 0 ? " [OUT OF STOCK]" : ""}
                    </option>
                  ))}
                </select>

                {/* Selected Picker Item Badge */}
                {pickerItem && (
                  <div
                    style={{
                      background: pickerItem.quantity > 0 ? "#F0FDF4" : "#FEF2F2",
                      border: `1px solid ${pickerItem.quantity > 0 ? "#BBF7D0" : "#FECACA"}`,
                      borderRadius: 6,
                      padding: "8px 12px",
                      marginBottom: 10,
                      fontSize: 12,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      <strong style={{ color: pickerItem.quantity > 0 ? "#166534" : "#991B1B" }}>
                        {pickerItem.name} {pickerItem.size ? `(${pickerItem.size})` : ""}
                      </strong>
                      <div style={{ color: "#4B5563", fontSize: 11, marginTop: 1 }}>
                        Available Store Stock: <strong>{pickerItem.quantity} {pickerItem.unit || "units"}</strong> | Base Rate: ₹{formatInr(pickerItem.rate)}
                      </div>
                    </div>
                    {toNumber(pickerQty) > 0 && toNumber(pickerRate) > 0 && (
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 10, color: "#6B7280" }}>Item Subtotal</div>
                        <strong style={{ fontSize: 13, color: "#976793" }}>
                          ₹ {formatInr(toNumber(pickerQty) * toNumber(pickerRate))}
                        </strong>
                      </div>
                    )}
                  </div>
                )}

                {/* Inline Qty, Rate, Note & Add Button */}
                <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1.2fr 2fr auto", gap: 8, alignItems: "end" }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: "#4B5563", display: "block", marginBottom: 3 }}>
                      Quantity to Allot <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={pickerItem?.quantity || undefined}
                      value={pickerQty}
                      onChange={(e) => setPickerQty(e.target.value)}
                      placeholder="Qty"
                      style={{
                        width: "100%",
                        padding: "7px 10px",
                        fontSize: 12,
                        borderRadius: 6,
                        border: "1px solid #D1D5DB",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: "#4B5563", display: "block", marginBottom: 3 }}>
                      Unit Rate (₹) <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={pickerRate}
                      onChange={(e) => setPickerRate(e.target.value)}
                      placeholder="Rate (₹)"
                      style={{
                        width: "100%",
                        padding: "7px 10px",
                        fontSize: 12,
                        borderRadius: 6,
                        border: "1px solid #D1D5DB",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: "#4B5563", display: "block", marginBottom: 3 }}>
                      Line Note / Purpose (Optional)
                    </label>
                    <input
                      type="text"
                      value={pickerNotes}
                      onChange={(e) => setPickerNotes(e.target.value)}
                      placeholder="e.g. Kitchen drawer channels"
                      style={{
                        width: "100%",
                        padding: "7px 10px",
                        fontSize: 12,
                        borderRadius: 6,
                        border: "1px solid #D1D5DB",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  <div>
                    <button
                      type="button"
                      onClick={handleAddItemToList}
                      disabled={!pickerItemId || !pickerItem || pickerItem.quantity <= 0}
                      style={{
                        padding: "8px 16px",
                        background: pickerItemId && pickerItem && pickerItem.quantity > 0 ? "#976793" : "#D1D5DB",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: pickerItemId && pickerItem && pickerItem.quantity > 0 ? "pointer" : "not-allowed",
                        whiteSpace: "nowrap",
                        height: 34,
                      }}
                    >
                      + Add Item to List
                    </button>
                  </div>
                </div>
              </div>

              {/* ── Section 2: Allotment Items List ── */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", textTransform: "uppercase" }}>
                    2. Allotment Items List ({itemsList.length} {itemsList.length === 1 ? "item" : "items"})
                  </div>
                  {itemsList.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setItemsList([])}
                      style={{ background: "none", border: "none", color: "#DC2626", fontSize: 11, cursor: "pointer", padding: 0 }}
                    >
                      Clear All Items
                    </button>
                  )}
                </div>

                {itemsList.length === 0 ? (
                  <div
                    style={{
                      border: "2px dashed #E5E7EB",
                      borderRadius: 8,
                      padding: "24px 16px",
                      textAlign: "center",
                      color: "#9CA3AF",
                      fontSize: 13,
                    }}
                  >
                    📦 No items added to list yet. Select an item above and click <strong>"+ Add Item to List"</strong>.
                  </div>
                ) : (
                  <div style={{ border: "1px solid #E5E7EB", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                        <thead>
                          <tr style={{ background: "#F9FAFB", borderBottom: "1px solid #E5E7EB", textAlign: "left" }}>
                            <th style={{ padding: "8px 10px", width: 30 }}>#</th>
                            <th style={{ padding: "8px 10px" }}>Item & Size</th>
                            <th style={{ padding: "8px 10px", width: 90 }}>In Stock</th>
                            <th style={{ padding: "8px 10px", width: 100 }}>Qty to Allot</th>
                            <th style={{ padding: "8px 10px", width: 110 }}>Rate (₹)</th>
                            <th style={{ padding: "8px 10px", width: 110, textAlign: "right" }}>Total (₹)</th>
                            <th style={{ padding: "8px 10px" }}>Line Note</th>
                            <th style={{ padding: "8px 10px", width: 40, textAlign: "center" }}>✕</th>
                          </tr>
                        </thead>
                        <tbody>
                          {itemsList.map((it, idx) => {
                            const q = toNumber(it.quantity, 0);
                            const r = toNumber(it.rate, 0);
                            const lineTotal = Math.round(q * r * 100) / 100;
                            const isQtyExceeded = q > it.maxStock;
                            const isQtyInvalid = q <= 0;

                            return (
                              <tr
                                key={idx}
                                style={{
                                  borderBottom: idx === itemsList.length - 1 ? "none" : "1px solid #F3F4F6",
                                  background: isQtyExceeded || isQtyInvalid ? "#FEF2F2" : "transparent",
                                }}
                              >
                                <td style={{ padding: "8px 10px", color: "#9CA3AF", fontWeight: 600 }}>{idx + 1}</td>
                                <td style={{ padding: "8px 10px" }}>
                                  <div style={{ fontWeight: 600, color: "#1F2937" }}>{it.name}</div>
                                  {it.size && <div style={{ fontSize: 11, color: "#6B7280" }}>{it.size}</div>}
                                </td>
                                <td style={{ padding: "8px 10px" }}>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      padding: "2px 6px",
                                      borderRadius: 4,
                                      fontSize: 10,
                                      fontWeight: 600,
                                      background: it.maxStock > 0 ? "#ECFDF5" : "#FEF2F2",
                                      color: it.maxStock > 0 ? "#047857" : "#B91C1C",
                                    }}
                                  >
                                    {it.maxStock} {it.unit}
                                  </span>
                                </td>
                                <td style={{ padding: "8px 10px" }}>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    max={it.maxStock}
                                    value={it.quantity}
                                    onChange={(e) => updateItemQty(idx, e.target.value)}
                                    style={{
                                      width: "100%",
                                      padding: "4px 6px",
                                      fontSize: 12,
                                      borderRadius: 4,
                                      border: isQtyExceeded || isQtyInvalid ? "1px solid #EF4444" : "1px solid #D1D5DB",
                                      background: isQtyExceeded || isQtyInvalid ? "#FFF" : "#fff",
                                      color: isQtyExceeded || isQtyInvalid ? "#DC2626" : "inherit",
                                      boxSizing: "border-box",
                                      fontWeight: 600,
                                    }}
                                    title={isQtyExceeded ? `Exceeds stock (${it.maxStock})` : ""}
                                  />
                                </td>
                                <td style={{ padding: "8px 10px" }}>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={it.rate}
                                    onChange={(e) => updateItemRate(idx, e.target.value)}
                                    style={{
                                      width: "100%",
                                      padding: "4px 6px",
                                      fontSize: 12,
                                      borderRadius: 4,
                                      border: "1px solid #D1D5DB",
                                      boxSizing: "border-box",
                                    }}
                                  />
                                </td>
                                <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: "#976793" }}>
                                  ₹ {formatInr(lineTotal)}
                                </td>
                                <td style={{ padding: "8px 10px" }}>
                                  <input
                                    type="text"
                                    value={it.notes || ""}
                                    onChange={(e) => updateItemNotes(idx, e.target.value)}
                                    placeholder="Note"
                                    style={{
                                      width: "100%",
                                      padding: "4px 6px",
                                      fontSize: 11,
                                      borderRadius: 4,
                                      border: "1px solid #E5E7EB",
                                      boxSizing: "border-box",
                                    }}
                                  />
                                </td>
                                <td style={{ padding: "8px 10px", textAlign: "center" }}>
                                  <button
                                    type="button"
                                    onClick={() => removeItem(idx)}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      color: "#EF4444",
                                      cursor: "pointer",
                                      fontSize: 14,
                                      padding: 0,
                                    }}
                                    title="Remove item from allotment list"
                                  >
                                    ✕
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Summary Bar */}
                    <div
                      style={{
                        padding: "10px 14px",
                        background: "#F5F3FF",
                        borderTop: "1px solid #DDD6FE",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div style={{ fontSize: 12, color: "#4B5563" }}>
                        Total Selected: <strong>{itemsList.length} items</strong> ({totalUnits} total units)
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <span style={{ fontSize: 11, color: "#6B7280", marginRight: 8 }}>Grand Total Value:</span>
                        <strong style={{ fontSize: 16, color: "#7C3AED" }}>₹ {formatInr(grandTotal)}</strong>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Order Destination & Notes ── */}
              {selectedClientId && (
                <div className="im-field" style={{ marginBottom: 14 }}>
                  <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>Purchase Expense Order Destination <span style={{ color: "#EF4444" }}>*</span></span>
                    {loadingOrders && <span style={{ fontSize: 10, color: "#6B7280" }}>Checking client orders...</span>}
                  </label>
                  <select
                    value={selectedOrderId}
                    onChange={(e) => setSelectedOrderId(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      borderRadius: 6,
                      border: "1px solid #D1D5DB",
                      fontSize: 12,
                    }}
                  >
                    <option value="new">
                      ➕ Create New Purchase Expense Order (Store Allotment — {itemsList.length} items, ₹{formatInr(grandTotal)})
                    </option>
                    {clientOrders.map((ord) => (
                      <option key={ord._id} value={ord._id}>
                        Add to: {ord.orderCode || "Order"} — {ord.title || "Procurement"} (Current Total: ₹{formatInr(ord.total)}) [{ord.status}]
                      </option>
                    ))}
                  </select>
                  <div style={{ fontSize: 11, color: "#6B7280", marginTop: 4 }}>
                    {selectedOrderId === "new"
                      ? `A new approved Purchase Order will be created under ${selectedClient?.name || "this client"} for all ${itemsList.length} item(s) totalling ₹${formatInr(grandTotal)}.`
                      : `These ${itemsList.length} items will be appended to the selected purchase order.`}
                  </div>
                </div>
              )}

              {/* General Work Area Notes */}
              <div className="im-field" style={{ marginBottom: 4 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                  Work Area / Purpose / Site Notes
                </label>
                <textarea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Master bedroom sliding wardrobe, Kitchen shutters, Site delivery batch #1"
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: 6,
                    border: "1px solid #D1D5DB",
                    fontSize: 12,
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>
          ) : (
            /* ── Further Steps View ── */
            <div>
              <div
                style={{
                  background: "#F0FDF4",
                  border: "1px solid #BBF7D0",
                  borderRadius: 10,
                  padding: "16px 20px",
                  marginBottom: 16,
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    background: "#DCFCE7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 22,
                    color: "#16A34A",
                    flexShrink: 0,
                  }}
                >
                  ✓
                </div>
                <div>
                  <h4 style={{ margin: 0, fontSize: 16, color: "#166534", fontWeight: 700 }}>
                    Material Allotment Confirmed Successfully!
                  </h4>
                  <p style={{ margin: "2px 0 0 0", fontSize: 12, color: "#374151" }}>
                    Stock has been auto-deducted from warehouse inventory and recorded into procurement.
                  </p>
                </div>
              </div>

              {/* KPI Details Cards */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 16 }}>
                <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", fontWeight: 600 }}>Customer</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#1F2937", marginTop: 2 }}>
                    {selectedClient?.name || "Customer"}
                  </div>
                  {selectedClient?.phone && <div style={{ fontSize: 11, color: "#9CA3AF" }}>{selectedClient.phone}</div>}
                </div>

                <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", fontWeight: 600 }}>Allotted Items</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#1F2937", marginTop: 2 }}>
                    {successData?.itemsCount || itemsList.length} products
                  </div>
                  <div style={{ fontSize: 11, color: "#059669" }}>{totalUnits} units deducted</div>
                </div>

                <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", fontWeight: 600 }}>Total Value</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#7C3AED", marginTop: 2 }}>
                    ₹ {formatInr(successData?.totalAmount || grandTotal)}
                  </div>
                  <div style={{ fontSize: 11, color: "#6B7280" }}>Procurement charge</div>
                </div>

                <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", fontWeight: 600 }}>Purchase Order</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1F2937", marginTop: 2 }}>
                    {successData?.order?.orderCode ? `PO: ${successData.order.orderCode}` : "Approved"}
                  </div>
                  <div style={{ fontSize: 11, color: "#15803D" }}>Status: Approved</div>
                </div>
              </div>

              {/* Items Allotted Table Preview */}
              <div style={{ border: "1px solid #E5E7EB", borderRadius: 8, overflow: "hidden", marginBottom: 18 }}>
                <div style={{ padding: "8px 12px", background: "#F9FAFB", fontSize: 11, fontWeight: 700, color: "#4B5563" }}>
                  DEDUCTED ITEMS BREAKDOWN
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <tbody>
                    {itemsList.map((it, idx) => (
                      <tr key={idx} style={{ borderBottom: idx === itemsList.length - 1 ? "none" : "1px solid #F3F4F6" }}>
                        <td style={{ padding: "8px 12px", fontWeight: 600, color: "#1F2937" }}>
                          {it.name} {it.size ? `(${it.size})` : ""}
                        </td>
                        <td style={{ padding: "8px 12px", color: "#059669", fontWeight: 600, textAlign: "right" }}>
                          {it.quantity} {it.unit}
                        </td>
                        <td style={{ padding: "8px 12px", color: "#6B7280", textAlign: "right" }}>
                          @ ₹{formatInr(it.rate)}
                        </td>
                        <td style={{ padding: "8px 12px", fontWeight: 700, color: "#976793", textAlign: "right" }}>
                          ₹ {formatInr(toNumber(it.quantity) * toNumber(it.rate))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Further Steps Actions Card */}
              <div
                style={{
                  background: "#FAF7FA",
                  border: "1px solid #E5D5E7",
                  borderRadius: 10,
                  padding: "16px 18px",
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, color: "#2E1A46", marginBottom: 6 }}>
                  🚀 Recommended Further Steps
                </div>
                <p style={{ margin: "0 0 12px 0", fontSize: 12, color: "#6B7280" }}>
                  Choose where to proceed next with this client or inventory:
                </p>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      if (selectedClientId) {
                        navigate(`/projects/${selectedClientId}`);
                      } else {
                        navigate("/projects");
                      }
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      padding: "10px 14px",
                      background: "#976793",
                      color: "#fff",
                      border: "none",
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                      textAlign: "center",
                    }}
                  >
                    📄 View Client Project & Orders
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      if (onOpenHistory) onOpenHistory();
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      padding: "10px 14px",
                      background: "#fff",
                      color: "#374151",
                      border: "1px solid #D1D5DB",
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                      textAlign: "center",
                    }}
                  >
                    📜 View Stock Movement History
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setItemsList([]);
                      setStep("form");
                      setSuccessData(null);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      padding: "10px 14px",
                      background: "#fff",
                      color: "#976793",
                      border: "1px solid #976793",
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                      textAlign: "center",
                    }}
                  >
                    📦 Allot More Items to Customer
                  </button>

                  <button
                    type="button"
                    onClick={onClose}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      padding: "10px 14px",
                      background: "#F3F4F6",
                      color: "#4B5563",
                      border: "1px solid #E5E7EB",
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                      textAlign: "center",
                    }}
                  >
                    ✓ Done / Return to Inventory
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer (Form Step only) */}
        {step === "form" && (
          <div className="im-modal-footer" style={{ padding: "14px 20px", borderTop: "1px solid #E5E7EB" }}>
            <button type="button" className="im-btn-ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              className="im-btn-submit"
              disabled={submitting || !selectedClientId || itemsList.length === 0 || hasInvalidItems}
              style={{
                background: submitting || !selectedClientId || itemsList.length === 0 || hasInvalidItems ? "#D1D5DB" : "#976793",
                cursor: submitting || !selectedClientId || itemsList.length === 0 || hasInvalidItems ? "not-allowed" : "pointer",
              }}
            >
              {submitting
                ? "Deducting Stock..."
                : `Confirm Allotment (${itemsList.length} ${itemsList.length === 1 ? "Item" : "Items"})`}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

// ── 3. Stock In / Receive Delivery Modal ─────────────────────────────────────
function ReceiveModal({ item, onClose, onReceived }) {
  const [quantity, setQuantity] = useState("");
  const [rate, setRate] = useState(item?.rate ? String(item.rate) : "");
  const [vendor, setVendor] = useState(item?.vendor || "");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    const q = toNumber(quantity);
    if (q <= 0) {
      setError("Received quantity must be greater than zero.");
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`${API}/${item._id}/receive`, {
        quantity: q,
        rate: rate ? toNumber(rate) : undefined,
        vendor: vendor.trim() || undefined,
        notes: notes.trim() || undefined,
      });

      onReceived();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to receive stock.");
    } finally {
      setSubmitting(false);
    }
  };

  const modalContent = (
    <div className="im-overlay" onClick={onClose}>
      <div className="im-modal" style={{ maxWidth: 500 }} onClick={(e) => e.stopPropagation()}>
        <div className="im-modal-header">
          <div>
            <h3>Receive Delivery / Stock In</h3>
            <p style={{ margin: "2px 0 0 0", fontSize: 12, color: "#6B7280" }}>
              Add newly purchased materials to available store stock.
            </p>
          </div>
          <button className="im-icon-btn" onClick={onClose} type="button" aria-label="Close">
            ✕
          </button>
        </div>

        <div className="im-modal-body">
          {error && (
            <div
              style={{
                background: "#FEF2F2",
                border: "1px solid #FECACA",
                color: "#991B1B",
                padding: "8px 12px",
                borderRadius: 6,
                marginBottom: 14,
                fontSize: 12,
              }}
            >
              {error}
            </div>
          )}

          <div
            style={{
              background: "#F9FAFB",
              border: "1px solid #E5E7EB",
              borderRadius: 8,
              padding: "10px 14px",
              marginBottom: 14,
              fontSize: 12,
            }}
          >
            <div>
              <strong>Product:</strong> {item.name} {item.size ? `(${item.size})` : ""}
            </div>
            <div style={{ marginTop: 2, color: "#4B5563" }}>
              Current Available Stock: <strong>{item.quantity} {item.unit || "units"}</strong>
            </div>
          </div>

          <form id="receive-form" onSubmit={submit}>
            <div className="im-field">
              <label>
                Quantity Received <span className="im-req">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="e.g. 20"
                required
              />
            </div>

            <div className="im-row">
              <div className="im-field">
                <label>Unit Purchase Rate (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  placeholder={`Default ₹${item.rate}`}
                />
              </div>

              <div className="im-field">
                <label>Vendor / Supplier</label>
                <input
                  value={vendor}
                  onChange={(e) => setVendor(e.target.value)}
                  placeholder="Supplier name"
                />
              </div>
            </div>

            <div className="im-field">
              <label>Delivery / Invoice / Challan Notes</label>
              <textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Challan #1049, Onyx delivery verified at warehouse"
              />
            </div>
          </form>
        </div>

        <div className="im-modal-footer">
          <button type="button" className="im-btn-ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" form="receive-form" className="im-btn-submit" disabled={submitting}>
            {submitting ? "Adding Stock..." : "Confirm Stock In (+)"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

// ── 4. Stock Movement Audit Log Modal ────────────────────────────────────────
function HistoryModal({ item, onClose }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = item ? { itemId: item._id, limit: 100 } : { limit: 100 };
    api
      .get("/inventory/transactions", { params })
      .then((res) => setTransactions(res.data?.data?.transactions || []))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [item]);

  const typeConfig = {
    opening: { label: "Opening Stock", bg: "#F3F4F6", color: "#374151" },
    purchase_in: { label: "Purchase In", bg: "#ECFDF5", color: "#047857" },
    issue_out: { label: "Allotted Out", bg: "#EFF6FF", color: "#1D4ED8" },
    return_in: { label: "Site Return", bg: "#FDF2F8", color: "#BE185D" },
    damage: { label: "Wastage / Damage", bg: "#FEF2F2", color: "#B91C1C" },
    adjustment: { label: "Adjustment", bg: "#FFFBEB", color: "#B45309" },
  };

  const modalContent = (
    <div className="im-overlay" onClick={onClose}>
      <div className="im-modal" style={{ maxWidth: 860 }} onClick={(e) => e.stopPropagation()}>
        <div className="im-modal-header">
          <div>
            <h3>{item ? `Stock Movements: ${item.name}` : "Global Stock Movement Audit Trail"}</h3>
            <p style={{ margin: "2px 0 0 0", fontSize: 12, color: "#6B7280" }}>
              Every stock change is recorded with date, quantity, type, related customer, and user.
            </p>
          </div>
          <button className="im-icon-btn" onClick={onClose} type="button" aria-label="Close">
            ✕
          </button>
        </div>

        <div className="im-modal-body" style={{ maxHeight: "65vh" }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center" }}>
              <div className="im-spinner" />
              <p style={{ marginTop: 8, color: "#6B7280", fontSize: 12 }}>Loading stock movements...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "#6B7280" }}>
              <p>No stock movement logs found for this item.</p>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#F9FAFB", borderBottom: "1px solid #E5E7EB", textAlign: "left" }}>
                  <th style={{ padding: "8px 10px", color: "#4B5563" }}>Date</th>
                  {!item && <th style={{ padding: "8px 10px", color: "#4B5563" }}>Item</th>}
                  <th style={{ padding: "8px 10px", color: "#4B5563" }}>Movement Type</th>
                  <th style={{ padding: "8px 10px", color: "#4B5563", textAlign: "right" }}>Quantity</th>
                  <th style={{ padding: "8px 10px", color: "#4B5563", textAlign: "right" }}>Rate (₹)</th>
                  <th style={{ padding: "8px 10px", color: "#4B5563" }}>Customer / Site</th>
                  <th style={{ padding: "8px 10px", color: "#4B5563" }}>User</th>
                  <th style={{ padding: "8px 10px", color: "#4B5563" }}>Notes / Reason</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => {
                  const conf = typeConfig[tx.type] || { label: tx.type, bg: "#F3F4F6", color: "#374151" };
                  const isNegative = tx.type === "issue_out" || tx.type === "damage" || tx.quantity < 0;
                  return (
                    <tr key={tx._id} style={{ borderBottom: "1px solid #F3F4F6" }}>
                      <td style={{ padding: "8px 10px", color: "#6B7280", whiteSpace: "nowrap" }}>
                        {tx.createdAt ? new Date(tx.createdAt).toLocaleDateString("en-IN") : "—"}
                      </td>
                      {!item && (
                        <td style={{ padding: "8px 10px", fontWeight: 600, color: "#1F2937" }}>
                          {tx.itemName} {tx.itemSize ? `(${tx.itemSize})` : ""}
                        </td>
                      )}
                      <td style={{ padding: "8px 10px" }}>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: 12,
                            fontSize: 10,
                            fontWeight: 600,
                            background: conf.bg,
                            color: conf.color,
                          }}
                        >
                          {conf.label}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: "8px 10px",
                          textAlign: "right",
                          fontWeight: 700,
                          color: isNegative ? "#DC2626" : "#16A34A",
                        }}
                      >
                        {isNegative ? "-" : "+"}{Math.abs(tx.quantity)}
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "right", color: "#6B7280" }}>
                        ₹ {formatInr(tx.rate)}
                      </td>
                      <td style={{ padding: "8px 10px", color: "#1F2937", fontWeight: 500 }}>
                        {tx.clientName || tx.projectName || "—"}
                      </td>
                      <td style={{ padding: "8px 10px", color: "#6B7280" }}>
                        {tx.createdByName || "Staff"}
                      </td>
                      <td style={{ padding: "8px 10px", color: "#4B5563", maxWidth: 180 }}>
                        {tx.notes || tx.reason || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="im-modal-footer">
          <button type="button" className="im-btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

// ── 5. Main Inventory Page Component ─────────────────────────────────────────
export default function Inventory() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [summary, setSummary] = useState({
    totalItems: 0,
    totalQuantity: 0,
    totalStockValue: 0,
    lowStockCount: 0,
  });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [lowStockFilter, setLowStockFilter] = useState(false);
  const [page, setPage] = useState(1);

  // Modals state
  const [showProductModal, setShowProductModal] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const [showAllotModal, setShowAllotModal] = useState(false);
  const [allotItem, setAllotItem] = useState(null);
  const [allotItemsInitial, setAllotItemsInitial] = useState([]);

  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [receiveItem, setReceiveItem] = useState(null);

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyItem, setHistoryItem] = useState(null);

  const [deleting, setDeleting] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkModal, setBulkModal] = useState(null);
  const [bulkValue, setBulkValue] = useState("");

  const isAdmin = user?.role === "admin" || user?.role === "manager";
  const LIMIT = 40;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (search) params.search = search;
      if (vendorFilter) params.vendor = vendorFilter;
      if (lowStockFilter) params.lowStock = "true";

      const res = await api.get(API, { params });
      setItems(res.data?.data?.items || []);
      setVendors(res.data?.data?.vendors || []);
      setSummary(res.data?.data?.summary || { totalItems: 0, totalQuantity: 0, totalStockValue: 0, lowStockCount: 0 });
      setTotal(res.data?.data?.pagination?.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, search, vendorFilter, lowStockFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => items.some((item) => item._id === id)));
  }, [items]);

  const openAdd = () => {
    setEditItem(null);
    setShowProductModal(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setShowProductModal(true);
  };

  const openAllot = (itemOrItems = null) => {
    if (Array.isArray(itemOrItems)) {
      setAllotItem(null);
      setAllotItemsInitial(itemOrItems);
    } else {
      setAllotItem(itemOrItems);
      setAllotItemsInitial(itemOrItems ? [itemOrItems] : []);
    }
    setShowAllotModal(true);
  };

  const openReceive = (item) => {
    setReceiveItem(item);
    setShowReceiveModal(true);
  };

  const openHistory = (item = null) => {
    setHistoryItem(item);
    setShowHistoryModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this inventory item?")) return;
    setDeleting(id);
    try {
      await api.delete(`${API}/${id}`);
      load();
    } catch (e) {
      alert(e.response?.data?.message || "Delete failed.");
    } finally {
      setDeleting(null);
    }
  };

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedItems = useMemo(() => items.filter((item) => selectedSet.has(item._id)), [items, selectedSet]);
  const allVisibleSelected = items.length > 0 && items.every((item) => selectedSet.has(item._id));

  const toggleSelected = (id, checked) => {
    setSelectedIds((prev) => (checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)));
  };
  const selectAllVisible = () => setSelectedIds(items.map((item) => item._id));
  const clearSelection = () => setSelectedIds([]);

  const handleBulkDelete = async () => {
    if (!isAdmin || selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected inventory item(s)?`)) return;
    try {
      await Promise.all(selectedIds.map((id) => api.delete(`${API}/${id}`)));
      clearSelection();
      load();
    } catch (e) {
      alert(e.response?.data?.message || "Bulk delete failed.");
    }
  };

  const applyBulkUpdate = async () => {
    const value = String(bulkValue || "").trim();
    try {
      await Promise.all(selectedItems.map((item) => api.put(`${API}/${item._id}`, { ...item, [bulkModal]: value })));
      setBulkModal(null);
      load();
    } catch (e) {
      alert(e.response?.data?.message || "Bulk update failed.");
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="im-root slide-up">
      {/* ── KPI Summary Cards ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: 14,
          padding: "16px 20px 0 20px",
          flexShrink: 0,
        }}
      >
        {/* KPI 1: Total Stock Value */}
        <div
          style={{
            background: "#fff",
            border: "1px solid #E5E7EB",
            borderRadius: 12,
            padding: "14px 18px",
            boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase" }}>
            Total Stock Value
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#976793", marginTop: 4 }}>
            ₹ {formatInr(summary.totalStockValue)}
          </div>
          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>
            Rate × Available Quantity in store
          </div>
        </div>

        {/* KPI 2: Total Items */}
        <div
          style={{
            background: "#fff",
            border: "1px solid #E5E7EB",
            borderRadius: 12,
            padding: "14px 18px",
            boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase" }}>
            Catalog Products
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#1F2937", marginTop: 4 }}>
            {summary.totalItems} items
          </div>
          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>
            Materials, hardware, and adhesives
          </div>
        </div>

        {/* KPI 3: Units on Hand */}
        <div
          style={{
            background: "#fff",
            border: "1px solid #E5E7EB",
            borderRadius: 12,
            padding: "14px 18px",
            boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase" }}>
            Available Store Units
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#1F2937", marginTop: 4 }}>
            {summary.totalQuantity.toLocaleString("en-IN")} units
          </div>
          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>
            Sheets, packets, sets, and pieces
          </div>
        </div>

        {/* KPI 4: Low Stock Alert */}
        <div
          style={{
            background: summary.lowStockCount > 0 ? "#FEF2F2" : "#F0FDF4",
            border: `1px solid ${summary.lowStockCount > 0 ? "#FECACA" : "#BBF7D0"}`,
            borderRadius: 12,
            padding: "14px 18px",
            boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
            cursor: "pointer",
          }}
          onClick={() => setLowStockFilter((v) => !v)}
          title="Click to toggle low stock filter"
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: summary.lowStockCount > 0 ? "#991B1B" : "#166534",
              textTransform: "uppercase",
            }}
          >
            Low Stock Alerts {lowStockFilter ? "(Filtered)" : ""}
          </div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: summary.lowStockCount > 0 ? "#DC2626" : "#16A34A",
              marginTop: 4,
            }}
          >
            {summary.lowStockCount} items
          </div>
          <div style={{ fontSize: 11, color: summary.lowStockCount > 0 ? "#B91C1C" : "#15803D", marginTop: 2 }}>
            {summary.lowStockCount > 0 ? "Requires procurement reorder" : "Stock levels are healthy"}
          </div>
        </div>
      </div>

      {/* ── Main Toolbar ── */}
      <div className="im-toolbar" style={{ marginTop: 14 }}>
        <div className="im-toolbar-left" style={{ gap: 10 }}>
          <button
            type="button"
            className="im-btn-add"
            onClick={openAdd}
            style={{ background: "#976793" }}
          >
            + Add Product (Procurement)
          </button>

          <button
            type="button"
            className="im-btn-export"
            onClick={() => openAllot(selectedItems.length > 0 ? selectedItems : null)}
            style={{ borderColor: "#976793", color: "#976793", fontWeight: 600 }}
          >
            📦 Allot Hardware to Customer {selectedItems.length > 0 ? `(${selectedItems.length} selected)` : ""}
          </button>

          <button
            type="button"
            className="im-btn-export"
            onClick={() => openHistory(null)}
          >
            📜 Stock Movement History
          </button>
        </div>

        <div className="im-toolbar-right" style={{ gap: 10 }}>
          {/* Vendor Filter */}
          {vendors.length > 0 && (
            <select
              value={vendorFilter}
              onChange={(e) => {
                setVendorFilter(e.target.value);
                setPage(1);
              }}
              style={{
                padding: "6px 12px",
                borderRadius: 8,
                border: "1px solid #E5E7EB",
                fontSize: 12,
                color: "#374151",
                background: "#fff",
              }}
            >
              <option value="">All Vendors</option>
              {vendors.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          )}

          {/* Low Stock Toggle Button */}
          <button
            type="button"
            onClick={() => {
              setLowStockFilter((v) => !v);
              setPage(1);
            }}
            style={{
              padding: "6px 12px",
              borderRadius: 8,
              border: `1px solid ${lowStockFilter ? "#DC2626" : "#E5E7EB"}`,
              fontSize: 12,
              fontWeight: 600,
              background: lowStockFilter ? "#FEF2F2" : "#fff",
              color: lowStockFilter ? "#DC2626" : "#6B7280",
              cursor: "pointer",
            }}
          >
            {lowStockFilter ? "✕ Show All Stock" : "⚠️ Low Stock Only"}
          </button>
        </div>
      </div>

      {/* ── Search Bar ── */}
      <div className="im-search-bar">
        <div className="im-search-wrap">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="im-search-ic"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            className="im-search-input"
            placeholder="Search material by name, size, vendor, note..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <span className="im-total-count">
          Showing {items.length} of {total} products
        </span>
      </div>

      {/* ── Products Table ── */}
      <div className="im-table-wrap">
        <table className="im-table" style={{ minWidth: 1100 }}>
          <thead>
            <tr>
              {isAdmin && (
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={(e) => (e.target.checked ? selectAllVisible() : clearSelection())}
                    aria-label="Select all visible products"
                  />
                </th>
              )}
              <th style={{ minWidth: 220 }}>Product Name</th>
              <th style={{ minWidth: 120 }}>Size / Spec</th>
              <th style={{ textAlign: "right", minWidth: 110 }}>Rate (₹)</th>
              <th style={{ textAlign: "right", minWidth: 110 }}>Available Qty</th>
              <th style={{ textAlign: "right", minWidth: 130 }}>Stock Value (₹)</th>
              <th style={{ minWidth: 160 }}>Vendor</th>
              <th style={{ minWidth: 180 }}>Note</th>
              <th style={{ textAlign: "center", minWidth: 220 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={isAdmin ? 9 : 8} className="im-td-center">
                  <div className="im-spinner" />
                  <p style={{ marginTop: 8, fontSize: 12, color: "#6B7280" }}>Loading inventory stock...</p>
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 9 : 8} className="im-td-center im-empty">
                  <p style={{ fontWeight: 600, margin: 0 }}>No inventory items found.</p>
                  <p style={{ fontSize: 12, color: "#9CA3AF", marginTop: 4 }}>
                    Click <strong>"+ Add Product"</strong> to record new materials from suppliers.
                  </p>
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const lowStock =
                  Number(item.quantity || 0) <= Number(item.minQuantity || 0) && Number(item.minQuantity || 0) > 0;
                const isZeroStock = Number(item.quantity || 0) <= 0;

                return (
                  <tr key={item._id}>
                    {isAdmin && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedSet.has(item._id)}
                          onChange={(e) => toggleSelected(item._id, e.target.checked)}
                          aria-label={`Select ${item.name}`}
                        />
                      </td>
                    )}

                    {/* Product Name */}
                    <td className="im-td-name">
                      <div style={{ fontWeight: 600, color: "#1F2937" }}>{item.name}</div>
                      {item.category && item.category !== "Material" && (
                        <div style={{ fontSize: 11, color: "#9CA3AF" }}>{item.category}</div>
                      )}
                    </td>

                    {/* Size */}
                    <td style={{ color: "#374151", fontWeight: 500 }}>{item.size || "—"}</td>

                    {/* Rate (₹) */}
                    <td style={{ textAlign: "right", color: "#374151" }}>
                      ₹ {formatInr(item.rate)}
                    </td>

                    {/* Available Qty */}
                    <td style={{ textAlign: "right" }}>
                      <span
                        className="im-pill"
                        style={{
                          background: isZeroStock ? "#FEF2F2" : lowStock ? "#FFFBEB" : "#ECFDF5",
                          color: isZeroStock ? "#DC2626" : lowStock ? "#B45309" : "#047857",
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        {Number(item.quantity || 0).toLocaleString("en-IN")} {item.unit || ""}
                      </span>
                    </td>

                    {/* Total Stock Value = Rate * Quantity */}
                    <td style={{ textAlign: "right", fontWeight: 700, color: "#976793" }}>
                      ₹ {formatInr(item.stockValue)}
                    </td>

                    {/* Vendor */}
                    <td style={{ color: "#4B5563" }}>{item.vendor || "—"}</td>

                    {/* Note */}
                    <td className="im-td-desc" style={{ fontSize: 12 }}>
                      {item.notes || "—"}
                    </td>

                    {/* Actions */}
                    <td className="im-td-actions" style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                      {/* Allot to Customer */}
                      <button
                        className="im-action-btn"
                        style={{
                          width: "auto",
                          padding: "0 8px",
                          background: "#EFF6FF",
                          borderColor: "#BFDBFE",
                          color: "#1D4ED8",
                          fontWeight: 600,
                          fontSize: 11,
                        }}
                        onClick={() => openAllot(item)}
                        disabled={item.quantity <= 0}
                        title={item.quantity <= 0 ? "Out of stock" : "Allot to Customer (auto-deducts stock)"}
                        type="button"
                      >
                        📦 Allot
                      </button>

                      {/* Stock In / Receive */}
                      <button
                        className="im-action-btn"
                        style={{
                          width: "auto",
                          padding: "0 8px",
                          background: "#ECFDF5",
                          borderColor: "#A7F3D0",
                          color: "#047857",
                          fontWeight: 600,
                          fontSize: 11,
                        }}
                        onClick={() => openReceive(item)}
                        title="Receive more delivery from vendor (+ stock)"
                        type="button"
                      >
                        📥 Stock In
                      </button>

                      {/* History */}
                      <button
                        className="im-action-btn"
                        style={{ width: "auto", padding: "0 8px", fontSize: 11 }}
                        onClick={() => openHistory(item)}
                        title="View stock movement logs"
                        type="button"
                      >
                        📜 History
                      </button>

                      {/* Edit */}
                      <button
                        className="im-action-btn im-edit"
                        style={{ width: "auto", padding: "0 8px", fontSize: 11 }}
                        onClick={() => openEdit(item)}
                        type="button"
                      >
                        Edit
                      </button>

                      {/* Delete */}
                      {isAdmin && (
                        <button
                          className="im-action-btn im-del"
                          style={{ width: "auto", padding: "0 8px", fontSize: 11 }}
                          onClick={() => handleDelete(item._id)}
                          disabled={deleting === item._id}
                          type="button"
                        >
                          ✕
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="im-pagination">
          <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="im-page-btn">
            Prev
          </button>
          <span className="im-page-info">
            Page {page} of {totalPages} — {total} products
          </span>
          <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)} className="im-page-btn">
            Next
          </button>
        </div>
      )}

      {/* ── Modals ── */}
      {showProductModal && (
        <ProductModal
          item={editItem}
          onClose={() => {
            setShowProductModal(false);
            setEditItem(null);
          }}
          onSaved={load}
        />
      )}

      {showAllotModal && (
        <AllotModal
          item={allotItem}
          initialItems={allotItemsInitial}
          allItems={items}
          onClose={() => {
            setShowAllotModal(false);
            setAllotItem(null);
            setAllotItemsInitial([]);
          }}
          onAllotted={load}
          onOpenHistory={() => openHistory(null)}
        />
      )}

      {showReceiveModal && receiveItem && (
        <ReceiveModal
          item={receiveItem}
          onClose={() => {
            setShowReceiveModal(false);
            setReceiveItem(null);
          }}
          onReceived={load}
        />
      )}

      {showHistoryModal && (
        <HistoryModal
          item={historyItem}
          onClose={() => {
            setShowHistoryModal(false);
            setHistoryItem(null);
          }}
        />
      )}

      {/* Bulk actions */}
      <BulkActionsBar
        count={selectedIds.length}
        actions={[
          { key: "allot", label: "Allot Selected", icon: bulkIcons.edit || "📦", onClick: () => openAllot(selectedItems) },
          { key: "vendor", label: "Set Vendor", icon: bulkIcons.set, onClick: () => { setBulkValue(""); setBulkModal("vendor"); } },
          { key: "category", label: "Set Category", icon: bulkIcons.set, onClick: () => { setBulkValue(""); setBulkModal("category"); } },
          { key: "unit", label: "Set Unit", icon: bulkIcons.set, onClick: () => { setBulkValue(""); setBulkModal("unit"); } },
          { key: "all", label: "Select All", icon: bulkIcons.selectAll, onClick: selectAllVisible },
          { key: "none", label: "Deselect All", icon: bulkIcons.clear, onClick: clearSelection },
          { key: "delete", label: "Delete", icon: bulkIcons.delete, onClick: handleBulkDelete, danger: true },
        ]}
      />

      {bulkModal && (
        <BulkValueModal
          title={`Set ${bulkModal}`}
          count={selectedIds.length}
          value={bulkValue}
          onChange={setBulkValue}
          onClose={() => setBulkModal(null)}
          onApply={applyBulkUpdate}
          placeholder={`Enter ${bulkModal}`}
        />
      )}
    </div>
  );
}
